import { TokenEntity } from '../../../domain/entities/token_entity';
import environment from '../../../../config/environment';

/**
 * Interface para datasource local de autenticação
 */
export interface AuthLocalDataSource {
  /**
   * Recupera token do storage local
   */
  getToken(): Promise<TokenEntity | null>;

  /**
   * Salva token no storage local
   */
  saveToken(token: TokenEntity | null): Promise<void>;

  /**
   * Remove token do storage local
   */
  removeToken(): Promise<void>;
}

/**
 * Implementação do datasource local usando cookies
 * O token é armazenado em cookies e enviado automaticamente para o servidor
 */
export class CookieAuthDataSource implements AuthLocalDataSource {
  // Primary cookie key (backend expects 'token')
  private readonly TOKEN_COOKIE_NAME = 'token';
  private readonly USER_COOKIE_NAME = 'auth_user';
  private readonly EXPIRES_COOKIE_NAME = 'auth_expires';

  async getToken(): Promise<TokenEntity | null> {
    try {
      console.log('🍪 Verificando cookies...');
      
  // Direct read (single key now 'token')
  let token = this.getCookie(this.TOKEN_COOKIE_NAME);
      const userStr = this.getCookie(this.USER_COOKIE_NAME);
      const expiresAtStr = this.getCookie(this.EXPIRES_COOKIE_NAME);
      
      console.log('🍪 Cookies encontrados:', { hasToken: !!token, hasUser: !!userStr, hasExpires: !!expiresAtStr });
      
      if (!token) {
        console.log('❌ Nenhum token encontrado nos cookies');
        return null;
      }

      const expiresAt = expiresAtStr ? new Date(expiresAtStr) : undefined;
      const user = userStr ? JSON.parse(decodeURIComponent(userStr)) : undefined;
      
      console.log('🔍 Token recuperado dos cookies:', { hasToken: !!token, hasUser: !!user, expiresAt });
      
      return { token, expiresAt, user };
    } catch (error) {
      console.error('Error getting token from cookies:', error);
      return null;
    }
  }

  async saveToken(tokenEntity: TokenEntity | null): Promise<void> {
    try {
      if (!tokenEntity) {
        await this.removeToken();
        return;
      }

      console.log('🍪 Salvando token nos cookies');

      // Define a expiração do cookie (30 dias por padrão ou baseado no token)
      const expiresAt = tokenEntity.expiresAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      
      // Salva o token principal
      this.setCookie(this.TOKEN_COOKIE_NAME, tokenEntity.token, expiresAt);
      
      // Salva dados do usuário se existir
      if (tokenEntity.user) {
        this.setCookie(this.USER_COOKIE_NAME, encodeURIComponent(JSON.stringify(tokenEntity.user)), expiresAt);
      }
      
      // Salva data de expiração
      if (tokenEntity.expiresAt) {
        this.setCookie(this.EXPIRES_COOKIE_NAME, tokenEntity.expiresAt.toISOString(), expiresAt);
      }
    } catch (error) {
      console.error('Error saving token to cookies:', error);
      throw error;
    }
  }

  async removeToken(): Promise<void> {
    try {
      console.log('🗑️ Removendo todos os cookies de autenticação...');
      
      this.deleteCookie(this.TOKEN_COOKIE_NAME);
      console.log(`✅ Cookie ${this.TOKEN_COOKIE_NAME} removido`);
      
      this.deleteCookie(this.USER_COOKIE_NAME);
      console.log(`✅ Cookie ${this.USER_COOKIE_NAME} removido`);
      
      this.deleteCookie(this.EXPIRES_COOKIE_NAME);
      console.log(`✅ Cookie ${this.EXPIRES_COOKIE_NAME} removido`);
      
      console.log('✅ Todos os cookies de autenticação foram removidos');
    } catch (error) {
      console.error('❌ Error removing token from cookies:', error);
      throw error;
    }
  }

  /**
   * Obtém um cookie pelo nome
   */
  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Define um cookie
   */
  private setCookie(name: string, value: string, expires: Date): void {
    // Detecta se estamos chamando uma API em outro domínio/porta (cross-site)
    const apiOrigin = (() => {
      try {
        const url = new URL((window as any).API_BASE_URL || '');
        return `${url.hostname}:${url.port}`;
      } catch {
        return null;
      }
    })();
    const currentHost = window.location.host;
    const crossSite = apiOrigin && apiOrigin !== currentHost;

    // Política de SameSite dinâmica:
    // - Development: SameSite=Lax (cookies locais, Authorization header para cross-domain)
    // - Production: SameSite=None com Secure (exige HTTPS)
    let sameSiteAttr = 'SameSite=Lax';
    let secureAttr = '';
    
    if (environment.isDevelopment) {
      // Development: Lax para cookies locais, Authorization header para APIs externas
      sameSiteAttr = 'SameSite=Lax';
      secureAttr = '';
    } else if (crossSite) {
      // Production cross-site: exige HTTPS
      if (window.location.protocol === 'https:') {
        sameSiteAttr = 'SameSite=None';
        secureAttr = 'Secure';
      } else {
        console.warn('⚠️ Ambiente HTTP sem HTTPS: navegador rejeitará SameSite=None; mantendo Lax.');
        sameSiteAttr = 'SameSite=Lax';
      }
    } else if (window.location.protocol === 'https:') {
      // Production same-site com HTTPS
      secureAttr = 'Secure';
    }

    const cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; ${sameSiteAttr}${secureAttr ? '; ' + secureAttr : ''}`;
    document.cookie = cookie;
    console.log('🍪 Cookie salvo:', { name, crossSite, sameSiteAttr, secure: !!secureAttr, env: environment.mode });
  }

  /**
   * Remove um cookie
   */
  private deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    
    // Verificar se o cookie foi realmente removido
    const stillExists = this.getCookie(name);
    if (stillExists) {
      console.warn(`⚠️ Cookie ${name} ainda existe após tentativa de remoção`);
      // Tentar remover com domínio explícito
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
    } else {
      console.log(`✅ Cookie ${name} removido com sucesso`);
    }
  }
}
